import { useEffect, useState } from "react";
import { getInstorderCounterList } from "src/services/order-api";

const LUPIN_COMPANY_CODE = 618;

interface UseCounterSelectionParams {
  companyCode: any;
  stockistId: any;
  uploadType: any;
  fuidStatusId: any;
  moduleCode?: any;
  existingCounterCode?: any;
  existingCounterName?: any;
  existingCustomerName?: any;
}

// Shared by invoice-detail.component.tsx and invoice-header.component.tsx: lets the user
// (re)select the counter, at status 1 ("Yet To Process") or 22 ("Others"), for a Non-Medvol invoice, an
// Institutional invoice, or a KAM invoice (module_code 4) for Lupin.
export const useCounterSelection = ({
  companyCode,
  stockistId,
  uploadType,
  fuidStatusId,
  moduleCode,
  existingCounterCode,
  existingCounterName,
  existingCustomerName,
}: UseCounterSelectionParams) => {
  const [counterList, setCounterList] = useState<any[]>([]);
  const [selectedCounter, setSelectedCounter] = useState<any>(null);
  const [isCounterModalOpen, setIsCounterModalOpen] = useState(false);
  const [tempSelectedCounter, setTempSelectedCounter] = useState<any>(null);

  // Status 1 = Yet To Process, 22 = Others
  const isNmvYetToProcess = [1, 22].includes(Number(fuidStatusId)) && (uploadType === "NMV" || uploadType === "INST" || (Number(moduleCode) === 4 && Number(companyCode) === LUPIN_COMPANY_CODE));
  // Plain Non-Medvol invoices (module_code 1) are walk-in customer sales with no real counter —
  // let the user type a customer name instead of forcing a pick from the counter master list.
  const allowCustomerNameEntry = uploadType === "NMV" && Number(moduleCode) !== 4;

  useEffect(() => {
    if (!isNmvYetToProcess || !companyCode || !stockistId) return;

    getInstorderCounterList(companyCode, undefined, stockistId)
      .then((res: any) => setCounterList(res?.data?.data || []))
      .catch((err: any) => console.log(err));
  }, [isNmvYetToProcess, companyCode, stockistId]);

  const openCounterModal = () => {
    setTempSelectedCounter(
      selectedCounter ||
      (existingCounterCode && { counter_name: existingCounterName, counter_code: existingCounterCode }) ||
      (existingCustomerName && { customer_name: existingCustomerName, isCustomerEntry: true }) ||
      null
    );
    setIsCounterModalOpen(true);
  };

  const closeCounterModal = () => setIsCounterModalOpen(false);

  const confirmCounterSelection = () => {
    setSelectedCounter(tempSelectedCounter);
    setIsCounterModalOpen(false);
  };

  // Spread into the INVOICE_ADD/INVOICE_EDIT payload — a no-op object unless this is the NMV/status-1
  // flow and a counter (or, for module_code-1 NMV, a customer name) was actually picked
  const counterPayloadFields = isNmvYetToProcess && selectedCounter
    ? selectedCounter.isCustomerEntry
      ? {
        customer_name: selectedCounter.customer_name,
        counter_code: "",
        counter_name: "",
        location_code: "",
        location_name: "",
        counter_gst_number: "",
        counter_dl_number: "",
        counter_mci_number: "",
      }
      : {
        counter_code: selectedCounter.counter_code,
        counter_name: selectedCounter.counter_name,
        location_code: selectedCounter.location_code,
        location_name: selectedCounter.location_name,
        // Validate against the counter actually selected, not the one being replaced. The screen already
        // displays these from selectedCounter, so sending the invoice's stored values contradicted the UI.
        counter_gst_number: selectedCounter.gst_number ?? "",
        counter_dl_number: selectedCounter.dl_number ?? "",
        counter_mci_number: selectedCounter.mci_register_number ?? "",
        // M10-14841: send counter_company_location_id only for non-MV (NMV) invoices, not the KAM/Lupin edge
        // case — and never when customer-name entry is offered, that flow doesn't carry a location link.
        ...(!allowCustomerNameEntry && { counter_company_location_id: selectedCounter.countercompanylnk_id }),
        // When customer-name entry is offered alongside counter selection, a picked counter
        // still needs to populate customer_name — with the selected counter's own name. Omit
        // the field entirely rather than send it empty when that's not the case.
        ...(allowCustomerNameEntry && { customer_name: selectedCounter.counter_name }),
      }
    : {};

  return {
    isNmvYetToProcess,
    allowCustomerNameEntry,
    counterList,
    selectedCounter,
    isCounterModalOpen,
    tempSelectedCounter,
    setTempSelectedCounter,
    openCounterModal,
    closeCounterModal,
    confirmCounterSelection,
    counterPayloadFields,
  };
};
